import type { FC } from 'react';
import type { GetStaticPaths, GetStaticProps } from 'next';
import Button from 'epn-ui/umd/dist/components/Button';

import Stepper from '@pages/faq/self-employed/Stepper';

import useI18n, { getLocalePaths } from '@i18n';
import externalLinks from '@config/external-links.json';
import {
    getNalogRegistrationSteps,
    getEpnRegistrationSteps,
} from '@pages/faq/self-employed/config';

// HOCs
import WithRole, { rolesEnum } from '@HOCs/WithRole';
import { companyName } from '../../../../config/globalDomain';

const SelfEmployedPage: FC = () => {
    const { t } = useI18n('Faq');

    const nalogRegistrationStepsConfig = getNalogRegistrationSteps(t);
    const withEpnRegistration = getEpnRegistrationSteps(t);

    return (
        <div className="flex flex-col w-full mb-[100px]">
            <h1 className="font-medium mb-[16px] xs:mb-[24px] text-blueGray-600 text-h2 md:text-h1">
                {t('Instruction for self-employed person')}
            </h1>
            <p className="mb-[16px] text-blueGray-500 text-[14px] md:mb-[24px]">
                {t('How to register as a self-employed person in the Federal Tax Service')}
            </p>
            <p className="mb-[16px] text-blueGray-500 text-[14px] md:mb-[24px]">
                {t('My taxes is an official application')}
            </p>
            <div className="flex flex-col mb-6 xs:flex-row sm:mb-8">
                <Button
                    type="default"
                    className="mb-4 xs:mb-0 xs:mr-6"
                    href={externalLinks.selfEmployedLaw}
                >
                    {t('Low on self-employed persons')}
                </Button>
                <Button type="primary" href={externalLinks.nalogApp}>
                    {t('Mobile application “My taxes”')}
                </Button>
            </div>
            <div className="mb-[16px] xs:mb-[24px]">
                <h2 className="mb-4 font-medium text-blueGray-600 text-sub xs:mb-6 md:mb-8 md:text-h2">
                    {t(
                        'How to register in the application “My taxes” through the taxpayer’s personal account',
                    )}
                </h2>
                <p className="mb-6 text-blueGray-500 text-[14px] md:mb-8">
                    {t('It’s the simplest way to register through any device')}
                </p>
                <Stepper {...nalogRegistrationStepsConfig} />
            </div>
            <div className="mb-8">
                <h2 className="mb-4 font-medium text-blueGray-600 text-sub xs:mb-6 md:mb-8 md:text-h2">
                    {t('How to register earnings from ePN', { companyName })}
                </h2>
                <Stepper {...withEpnRegistration} />
            </div>
            <p className="mb-20 font-medium text-center text-blueGray-600 text-p1 sm:text-sub">
                {t('You have registered your income and can pay tax on it')}
            </p>
        </div>
    );
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
    return { props: { locale: params?.locale } };
};

export const getStaticPaths: GetStaticPaths = async () => {
    return { paths: getLocalePaths(), fallback: false };
};

export default WithRole(SelfEmployedPage, [rolesEnum.guest]);
